import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "../../../../utils/notifications";
import { bulkUpdateRfqItems } from "../../../../services/rfqItems";
import { queryKeys } from "../../../../api/queryKeys";
import type { RFQ, RFQItem } from "../../../../types/rfq";
import type { TeamMember } from "../../../../types";

/** Items render in their source order (order_index), falling back to their id. */
const byOrder = (item: RFQItem) => item.order_index ?? item.rfq_item_id ?? 0;

/** An RFQ's items, each id once (the first wins), in source order. */
export function orderedUniqueItemsOf(items: RFQItem[] | undefined): RFQItem[] {
  const seen = new Set<number>();
  const unique = (items || []).filter((it) => {
    const id = it.rfq_item_id;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return unique.slice().sort((a, b) => byOrder(a) - byOrder(b));
}

/**
 * The RFQ row's item list: its items in order, which are ticked, and the bulk
 * actions on them (select every unvalidated item, clear, mark the ticked ones
 * not available - $0 and validated - in one backend call).
 */
export function useRfqItemsSelection(rfq: RFQ, selectedUser: TeamMember | null) {
  const queryClient = useQueryClient();
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);

  const orderedUniqueItems = useMemo(() => orderedUniqueItemsOf(rfq.items), [rfq.items]);

  const toggleItem = (itemId: number) => {
    setSelectedItemIds(prev => (prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]));
  };

  const selectAllUnvalidated = () => {
    const unvalidatedIds = orderedUniqueItems
      .filter((item) => !item.price_validated)
      .map((item) => item.rfq_item_id);
    setSelectedItemIds(unvalidatedIds);

    toast({
      title: "Items selected",
      description: `Selected ${unvalidatedIds.length} unvalidated item${unvalidatedIds.length !== 1 ? 's' : ''}`,
      severity: "info",
    });
  };

  const clearSelection = () => {
    setSelectedItemIds([]);
  };

  const markSelectedNotAvailable = async () => {
    if (!selectedUser) {
      toast({
        title: "Error",
        description: "No user selected. Please select a user before marking items.",
        severity: "error",
      });
      return;
    }

    if (selectedItemIds.length === 0) {
      toast({
        title: "No items selected",
        description: "Please select at least one item to mark as not available.",
        severity: "warning",
      });
      return;
    }

    try {
      const now = new Date().toISOString();
      const updateData = {
        quoted_price: 0,
        price_validated: true,
        validated_by: selectedUser.name,
        validated_at: now,
      };

      // Update all selected items, in one backend call
      await bulkUpdateRfqItems(rfq.rfq_id, selectedItemIds, updateData);

      queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });
      setSelectedItemIds([]);

      toast({
        title: "Success",
        description: `Marked ${selectedItemIds.length} item${selectedItemIds.length !== 1 ? 's' : ''} as not available`,
        severity: "success",
      });
    } catch (error) {
      console.error('Error marking items as not available:', error);

      // Invalidate queries to ensure UI is in sync with database
      queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });

      toast({
        title: "Error",
        description: "Failed to mark items as not available. Please try again.",
        severity: "error",
      });
    }
  };

  return { orderedUniqueItems, selectedItemIds, toggleItem, selectAllUnvalidated, clearSelection, markSelectedNotAvailable };
}

export type RfqItemsSelection = ReturnType<typeof useRfqItemsSelection>;
